import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  useColorModeValue,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  IconButton,
  Alert,
  AlertIcon,
  AlertDescription,
  Badge,
  Select,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
// Import icons
import { AddIcon, DeleteIcon, EmailIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { doc, setDoc, serverTimestamp, collection, getDocs, deleteDoc, getDoc, onSnapshot, query, orderBy, updateDoc, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import PageShell from "../layout/PageShell";
import { DEFAULT_POS_RECEIPT_TEMPLATE, fillPosReceiptShell, PosReceiptEmailTemplate } from "./posReceiptEmailDefaults";
import { STATION_REGISTRATION_TEMPLATES } from "./stationRegistrationTemplates";
import { DOCUMENT_EMAIL_MARK, DOCUMENT_EMAIL_TEMPLATES } from "./documentEmailDefaults";
import { MEMBER_VERIFICATION_TEMPLATES } from "./memberVerificationTemplates";
import { PASSWORD_RESET_DESIGN_MARK, PASSWORD_RESET_TEMPLATES } from "./passwordResetTemplates";
import { LogoCatalogModal } from "./LogoCatalog";
import { LogoFile, applyEmailLogo, BRAND_LOGO_COLLECTION, BRAND_LOGO_DOC_ID, previewSrcForUrl } from "./logoSeries";

function compactTemplateLabel(name: string): string {
  const french = /_fr$/i.test(name);
  const label = name
    .replace(/_fr$/i, "")
    .replace(/reservation/gi, "Rsv")
    .replace(/reschedul(?:e|ed)/gi, "Rsch")
    .replace(/_/g, " ")
    .replace(/\bcustomer\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
  return french ? `${label} FR` : label || name;
}

const EMAIL_TEMPLATES_COLLECTION = "emailTemplates";
const EMAIL_CONFIG_COLLECTION = "emailConfig";
const EMAIL_TEST_LOGS_COLLECTION = "emailTestLogs";

const DEFAULT_ORDER_CREATED_TEMPLATE_ID = "order_created_customer";
const DEFAULT_ORDER_CREATED_TEMPLATE_ID_FR = "order_created_customer_fr";
const DEFAULT_ORDER_CANCELLED_TEMPLATE_ID = "order_cancelled_customer";
const DEFAULT_ORDER_CANCELLED_TEMPLATE_ID_FR = "order_cancelled_customer_fr";
const ORDER_DESIGN_MARK = "nuage-order-design:1";
const DEFAULT_RESERVATION_CREATED_TEMPLATE_ID = "reservation_created_customer";
const DEFAULT_RESERVATION_CREATED_TEMPLATE_ID_FR = "reservation_created_customer_fr";
const DEFAULT_RESERVATION_CONFIRMED_TEMPLATE_ID = "reservation_confirmed_customer";
const DEFAULT_RESERVATION_CONFIRMED_TEMPLATE_ID_FR = "reservation_confirmed_customer_fr";
const DEFAULT_RESERVATION_CANCELLED_TEMPLATE_ID = "reservation_cancelled_customer";
const DEFAULT_RESERVATION_CANCELLED_TEMPLATE_ID_FR = "reservation_cancelled_customer_fr";
const DEFAULT_RESERVATION_RESCHEDULED_TEMPLATE_ID = "reservation_rescheduled_customer";
const DEFAULT_RESERVATION_RESCHEDULED_TEMPLATE_ID_FR = "reservation_rescheduled_customer_fr";

const orderEmailDocument = (copy: {
  lang: "en" | "fr";
  heading: string;
  orderNumber: string;
  service: string;
  ready: string;
  payment: string;
  name: string;
  phone: string;
  emailAddress: string;
  track: string;
  thanks: string;
  footer: string;
}) => {
  const shell: PosReceiptEmailTemplate = {
    ...DEFAULT_POS_RECEIPT_TEMPLATE,
    headingEn: copy.heading,
    headingFr: copy.heading,
    footerEn: copy.footer,
    footerFr: copy.footer,
  };
  const row = (label: string, token: string) => `<tr>
        <td style="padding:12px 0;border-bottom:1px solid #e5e7eb;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
            <tr>
              <td style="font-size:14px;color:#6b7280;">${label}</td>
              <td align="right" style="font-size:15px;font-weight:bold;color:#111827;">${token}</td>
            </tr>
          </table>
        </td>
      </tr>`;
  const body = `<!-- ${ORDER_DESIGN_MARK} -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="font-family:Arial,Helvetica,sans-serif;">
  <tr><td style="padding:8px 0 4px 0;font-size:16px;line-height:24px;color:#111827;">{{greeting}}</td></tr>
  <tr><td style="padding:0 0 16px 0;font-size:15px;line-height:22px;color:#374151;">{{lead}}</td></tr>
  <tr><td style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:10px;padding:8px 16px 4px 16px;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
      <tr>
        <td style="padding:14px 0 2px 0;font-size:12px;letter-spacing:0.04em;text-transform:uppercase;color:#6b7280;">${copy.orderNumber}</td>
      </tr>
      <tr>
        <td style="padding:0 0 12px 0;border-bottom:1px solid #e5e7eb;font-size:20px;line-height:26px;font-weight:bold;color:#111827;">{{orderNumber}}</td>
      </tr>
      ${row(copy.service, "{{serviceLabel}}")}
      ${row(copy.ready, "{{readyTime}}")}
      ${row(copy.payment, "{{paymentMethod}}")}
      ${row(copy.name, "{{customerName}}")}
      ${row(copy.phone, "{{customerPhone}}")}
      ${row(copy.emailAddress, "{{customerEmail}}")}
      {{fulfillmentBlock}}
    </table>
  </td></tr>
  {{itemsBlock}}
  {{totalsBlock}}
  <tr><td style="padding:20px 0 4px 0;">
    <a href="{{trackUrl}}" style="display:inline-block;background:#0ea5e9;color:#ffffff;font-family:Arial,Helvetica,sans-serif;font-size:14px;font-weight:bold;text-decoration:none;padding:12px 18px;border-radius:8px;">${copy.track}</a>
  </td></tr>
  <tr><td style="padding:8px 0 0 0;font-size:14px;line-height:20px;color:#6b7280;">${copy.thanks}</td></tr>
</table>`;
  return fillPosReceiptShell(shell, copy.lang, body, "{{storeName}}");
};

const getDefaultOrderCreatedTemplate = () => {
  return {
    id: DEFAULT_ORDER_CREATED_TEMPLATE_ID,
    name: "order_created_customer",
    subject: "Order {{orderNumber}} received - {{storeName}}",
    htmlContent: orderEmailDocument({
      lang: "en",
      heading: "Order received",
      orderNumber: "Order number",
      service: "Service",
      ready: "Ready time",
      payment: "Payment",
      name: "Name",
      phone: "Phone",
      emailAddress: "Email",
      track: "Track Your Order",
      thanks: "Thank you.",
      footer: "This message confirms an online order with the merchant.",
    }),
  };
};

const getDefaultOrderCreatedTemplateFr = () => {
  return {
    id: DEFAULT_ORDER_CREATED_TEMPLATE_ID_FR,
    name: "order_created_customer_fr",
    subject: "Commande {{orderNumber}} reçue - {{storeName}}",
    htmlContent: orderEmailDocument({
      lang: "fr",
      heading: "Commande reçue",
      orderNumber: "Numéro de commande",
      service: "Service",
      ready: "Heure prévue",
      payment: "Paiement",
      name: "Nom",
      phone: "Téléphone",
      emailAddress: "Courriel",
      track: "Suivre la commande",
      thanks: "Merci.",
      footer: "Ce message confirme une commande en ligne auprès du commerçant.",
    }),
  };
};

const getDefaultOrderCancelledTemplate = () => {
  return {
    id: DEFAULT_ORDER_CANCELLED_TEMPLATE_ID,
    name: "order_cancelled_customer",
    subject: "Order {{orderNumber}} cancelled - {{storeName}}",
    htmlContent: orderEmailDocument({
      lang: "en",
      heading: "Order cancelled",
      orderNumber: "Order number",
      service: "Service",
      ready: "Ready time",
      payment: "Payment",
      name: "Name",
      phone: "Phone",
      emailAddress: "Email",
      track: "View order",
      thanks: "Thank you.",
      footer: "This message confirms that an online order was cancelled.",
    }),
  };
};

const getDefaultOrderCancelledTemplateFr = () => {
  return {
    id: DEFAULT_ORDER_CANCELLED_TEMPLATE_ID_FR,
    name: "order_cancelled_customer_fr",
    subject: "Commande {{orderNumber}} annulée - {{storeName}}",
    htmlContent: orderEmailDocument({
      lang: "fr",
      heading: "Commande annulée",
      orderNumber: "Numéro de commande",
      service: "Service",
      ready: "Heure prévue",
      payment: "Paiement",
      name: "Nom",
      phone: "Téléphone",
      emailAddress: "Courriel",
      track: "Voir la commande",
      thanks: "Merci.",
      footer: "Ce message confirme qu'une commande en ligne a été annulée.",
    }),
  };
};

const RESERVATION_DESIGN_MARK = "nuage-reservation-design:2";
const RESERVATION_CANCEL_DESIGN_MARK = "nuage-reservation-cancel-design:1";
const RESERVATION_RESCHEDULE_DESIGN_MARK = "nuage-reservation-reschedule-design:1";
const RESERVATION_OWNER_REVIEW_DESIGN_MARK = "nuage-reservation-owner-review-design:1";
const RESERVATION_PROPOSAL_DESIGN_MARK = "nuage-reservation-proposal-design:1";
const DEFAULT_RESERVATION_OWNER_REVIEW_TEMPLATE_ID = "reservation_owner_review";
const DEFAULT_RESERVATION_OWNER_REVIEW_TEMPLATE_ID_FR = "reservation_owner_review_fr";
const DEFAULT_RESERVATION_PROPOSAL_TEMPLATE_ID = "reservation_time_proposed_customer";
const DEFAULT_RESERVATION_PROPOSAL_TEMPLATE_ID_FR = "reservation_time_proposed_customer_fr";
const DEFAULT_POS_CLOSING_RECEIPT_TEMPLATE_ID = "pos_closing_receipt";

type TemplateGroupId = "user" | "order" | "reservation" | "station" | "document";

const TEMPLATE_GROUPS: { id: TemplateGroupId; label: string }[] = [
  { id: "user", label: "User email" },
  { id: "order", label: "Order email" },
  { id: "reservation", label: "Reservation email" },
  { id: "station", label: "Station registration email" },
  { id: "document", label: "Document email" },
];

function isPosClosingTemplate(template: { id: string; name: string }) {
  return template.id === DEFAULT_POS_CLOSING_RECEIPT_TEMPLATE_ID
    || template.name.replace(/\s+/g, "") === "POSClosingReceipt";
}

function templateGroupId(template: { id: string; name: string; group?: string }): TemplateGroupId {
  if (template.group === "user" || template.group === "order" || template.group === "reservation" || template.group === "station" || template.group === "document") return template.group;
  const key = `${template.id} ${template.name}`.toLowerCase();
  if (key.includes("document_quotation") || key.includes("document_estimate") || key.includes("document_contract")) return "document";
  if (key.includes("station_registration") || key.includes("station registration")) return "station";
  if (key.includes("reservation") || key.includes("owner_review") || key.includes("owner review")) return "reservation";
  if (key.includes("order")) return "order";
  return "user";
}

const BUILTIN_TEMPLATE_IDS = new Set([
  DEFAULT_ORDER_CREATED_TEMPLATE_ID,
  DEFAULT_ORDER_CREATED_TEMPLATE_ID_FR,
  DEFAULT_ORDER_CANCELLED_TEMPLATE_ID,
  DEFAULT_ORDER_CANCELLED_TEMPLATE_ID_FR,
  DEFAULT_RESERVATION_CREATED_TEMPLATE_ID,
  DEFAULT_RESERVATION_CREATED_TEMPLATE_ID_FR,
  DEFAULT_RESERVATION_CONFIRMED_TEMPLATE_ID,
  DEFAULT_RESERVATION_CONFIRMED_TEMPLATE_ID_FR,
  DEFAULT_RESERVATION_CANCELLED_TEMPLATE_ID,
  DEFAULT_RESERVATION_CANCELLED_TEMPLATE_ID_FR,
  DEFAULT_RESERVATION_RESCHEDULED_TEMPLATE_ID,
  DEFAULT_RESERVATION_RESCHEDULED_TEMPLATE_ID_FR,
  DEFAULT_RESERVATION_OWNER_REVIEW_TEMPLATE_ID,
  DEFAULT_RESERVATION_OWNER_REVIEW_TEMPLATE_ID_FR,
  DEFAULT_RESERVATION_PROPOSAL_TEMPLATE_ID,
  DEFAULT_RESERVATION_PROPOSAL_TEMPLATE_ID_FR,
  ...STATION_REGISTRATION_TEMPLATES.map((template) => template.id),
  ...DOCUMENT_EMAIL_TEMPLATES.map((template) => template.id),
  ...MEMBER_VERIFICATION_TEMPLATES.map((template) => template.id),
  ...PASSWORD_RESET_TEMPLATES.map((template) => template.id),
]);

const reservationEmailDocument = (copy: {
  lang: "en" | "fr";
  heading: string;
  hello: string;
  lead: string;
  date: string;
  time: string;
  guests: string;
  table: string;
  reference: string;
  reschedule?: string;
  cancel?: string;
  phone?: string;
  emailAddress?: string;
  notesLabel?: string;
  offered?: string;
  review?: string;
  thanks: string;
  footer: string;
  designMark?: string;
}) => {
  const shell: PosReceiptEmailTemplate = {
    ...DEFAULT_POS_RECEIPT_TEMPLATE,
    headingEn: copy.heading,
    headingFr: copy.heading,
    footerEn: copy.footer,
    footerFr: copy.footer,
  };
  const extraRow = (label: string | undefined, token: string) => {
    if (!label) return "";
    return `<tr>
        <td style="padding:12px 0;border-bottom:1px solid #e5e7eb;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
            <tr>
              <td style="font-size:14px;color:#6b7280;">${label}</td>
              <td align="right" style="font-size:15px;font-weight:bold;color:#111827;">${token}</td>
            </tr>
          </table>
        </td>
      </tr>`;
  };
  const actions = copy.reschedule && copy.cancel
    ? `<tr><td style="padding:20px 0 4px 0;">
    <a href="{{rescheduleUrl}}" style="display:inline-block;background:#0ea5e9;color:#ffffff;font-family:Arial,Helvetica,sans-serif;font-size:14px;font-weight:bold;text-decoration:none;padding:12px 18px;border-radius:8px;margin:0 8px 8px 0;">${copy.reschedule}</a>
    <a href="{{cancelUrl}}" style="display:inline-block;background:#ffffff;color:#111827;border:1px solid #d1d5db;font-family:Arial,Helvetica,sans-serif;font-size:14px;font-weight:bold;text-decoration:none;padding:12px 18px;border-radius:8px;margin:0 0 8px 0;">${copy.cancel}</a>
  </td></tr>`
    : "";
  const review = copy.review
    ? `<tr><td style="padding:20px 0 4px 0;">
    <a href="{{reviewUrl}}" style="display:inline-block;background:#0ea5e9;color:#ffffff;font-family:Arial,Helvetica,sans-serif;font-size:14px;font-weight:bold;text-decoration:none;padding:12px 18px;border-radius:8px;">${copy.review}</a>
  </td></tr>`
    : "";
  const body = `<!-- ${copy.designMark || RESERVATION_DESIGN_MARK} -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="font-family:Arial,Helvetica,sans-serif;">
  <tr><td style="padding:8px 0 4px 0;font-size:16px;line-height:24px;color:#111827;">${copy.hello}{{customerName}},</td></tr>
  <tr><td style="padding:0 0 16px 0;font-size:15px;line-height:22px;color:#374151;">${copy.lead}</td></tr>
  <tr><td style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:10px;padding:8px 16px 4px 16px;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
      <tr>
        <td style="padding:14px 0 2px 0;font-size:12px;letter-spacing:0.04em;text-transform:uppercase;color:#6b7280;">${copy.date}</td>
      </tr>
      <tr>
        <td style="padding:0 0 12px 0;border-bottom:1px solid #e5e7eb;font-size:20px;line-height:26px;font-weight:bold;color:#111827;">{{reservationDate}}</td>
      </tr>
      <tr>
        <td style="padding:12px 0;border-bottom:1px solid #e5e7eb;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
            <tr>
              <td style="font-size:14px;color:#6b7280;">${copy.time}</td>
              <td align="right" style="font-size:16px;font-weight:bold;color:#0ea5e9;">{{reservationTime}}</td>
            </tr>
          </table>
        </td>
      </tr>
      <tr>
        <td style="padding:12px 0;border-bottom:1px solid #e5e7eb;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
            <tr>
              <td style="font-size:14px;color:#6b7280;">${copy.guests}</td>
              <td align="right" style="font-size:15px;font-weight:bold;color:#111827;">{{numberOfGuests}}</td>
            </tr>
          </table>
        </td>
      </tr>
      <tr>
        <td style="padding:12px 0;border-bottom:1px solid #e5e7eb;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
            <tr>
              <td style="font-size:14px;color:#6b7280;">${copy.table}</td>
              <td align="right" style="font-size:15px;font-weight:bold;color:#111827;">{{tableName}}</td>
            </tr>
          </table>
        </td>
      </tr>
      ${extraRow(copy.phone, "{{customerPhone}}")}
      ${extraRow(copy.emailAddress, "{{customerEmail}}")}
      ${extraRow(copy.notesLabel, "{{notes}}")}
      ${extraRow(copy.offered, "{{proposedSchedule}}")}
      <tr>
        <td style="padding:12px 0 14px 0;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
            <tr>
              <td style="font-size:14px;color:#6b7280;">${copy.reference}</td>
              <td align="right" style="font-size:15px;font-weight:bold;color:#111827;">{{reservationId}}</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </td></tr>
  ${actions}
  ${review}
  <tr><td style="padding:8px 0 0 0;font-size:14px;line-height:20px;color:#6b7280;">${copy.thanks}</td></tr>
</table>`;
  return fillPosReceiptShell(shell, copy.lang, body, "{{storeName}}");
};

const getDefaultReservationConfirmedTemplate = () => {
  return {
    id: DEFAULT_RESERVATION_CONFIRMED_TEMPLATE_ID,
    name: "reservation_confirmed_customer",
    subject: "Reservation confirmed - {{storeName}} - {{reservationDate}} {{reservationTime}}",
    htmlContent: reservationEmailDocument({
      lang: "en",
      heading: "Reservation confirmed",
      hello: "Hello ",
      lead: "Your reservation is confirmed.",
      date: "Date",
      time: "Time",
      guests: "Party size",
      table: "Table",
      reference: "Reference",
      reschedule: "Reschedule",
      cancel: "Cancel",
      thanks: "Thank you.",
      footer: "This message confirms a reservation with the merchant.",
    }),
  };
};

const getDefaultReservationConfirmedTemplateFr = () => {
  return {
    id: DEFAULT_RESERVATION_CONFIRMED_TEMPLATE_ID_FR,
    name: "reservation_confirmed_customer_fr",
    subject: "Réservation confirmée - {{storeName}} - {{reservationDate}} {{reservationTime}}",
    htmlContent: reservationEmailDocument({
      lang: "fr",
      heading: "Réservation confirmée",
      hello: "Bonjour ",
      lead: "Votre réservation est confirmée.",
      date: "Date",
      time: "Heure",
      guests: "Nombre de personnes",
      table: "Table",
      reference: "Référence",
      reschedule: "Reporter",
      cancel: "Annuler",
      thanks: "Merci.",
      footer: "Ce message confirme une réservation auprès du commerçant.",
    }),
  };
};

const getDefaultReservationCancelledTemplate = () => {
  return {
    id: DEFAULT_RESERVATION_CANCELLED_TEMPLATE_ID,
    name: "reservation_cancelled_customer",
    subject: "Reservation cancelled - {{storeName}} - {{reservationDate}} {{reservationTime}}",
    htmlContent: reservationEmailDocument({
      lang: "en",
      heading: "Reservation cancelled",
      hello: "Hello ",
      lead: "This reservation has been cancelled.",
      date: "Date",
      time: "Time",
      guests: "Party size",
      table: "Table",
      reference: "Reference",
      thanks: "Thank you.",
      footer: "This message confirms that a reservation with the merchant has been cancelled.",
      designMark: RESERVATION_CANCEL_DESIGN_MARK,
    }),
  };
};

const getDefaultReservationCancelledTemplateFr = () => {
  return {
    id: DEFAULT_RESERVATION_CANCELLED_TEMPLATE_ID_FR,
    name: "reservation_cancelled_customer_fr",
    subject: "Réservation annulée - {{storeName}} - {{reservationDate}} {{reservationTime}}",
    htmlContent: reservationEmailDocument({
      lang: "fr",
      heading: "Réservation annulée",
      hello: "Bonjour ",
      lead: "Cette réservation a été annulée.",
      date: "Date",
      time: "Heure",
      guests: "Nombre de personnes",
      table: "Table",
      reference: "Référence",
      thanks: "Merci.",
      footer: "Ce message confirme l'annulation d'une réservation auprès du commerçant.",
      designMark: RESERVATION_CANCEL_DESIGN_MARK,
    }),
  };
};

const getDefaultReservationRescheduledTemplate = () => {
  return {
    id: DEFAULT_RESERVATION_RESCHEDULED_TEMPLATE_ID,
    name: "reservation_rescheduled_customer",
    subject: "Reservation rescheduled - {{storeName}} - {{reservationDate}} {{reservationTime}}",
    htmlContent: reservationEmailDocument({
      lang: "en",
      heading: "Reservation rescheduled",
      hello: "Hello ",
      lead: "This reservation has been rescheduled from {{previousSchedule}} to {{reservationDate}} at {{reservationTime}}.",
      date: "Date",
      time: "Time",
      guests: "Party size",
      table: "Table",
      reference: "Reference",
      thanks: "Thank you.",
      footer: "This message confirms a change to a reservation with the merchant.",
      designMark: RESERVATION_RESCHEDULE_DESIGN_MARK,
    }),
  };
};

const getDefaultReservationRescheduledTemplateFr = () => {
  return {
    id: DEFAULT_RESERVATION_RESCHEDULED_TEMPLATE_ID_FR,
    name: "reservation_rescheduled_customer_fr",
    subject: "Réservation reportée - {{storeName}} - {{reservationDate}} {{reservationTime}}",
    htmlContent: reservationEmailDocument({
      lang: "fr",
      heading: "Réservation reportée",
      hello: "Bonjour ",
      lead: "Cette réservation a été reportée du {{previousSchedule}} au {{reservationDate}} à {{reservationTime}}.",
      date: "Date",
      time: "Heure",
      guests: "Nombre de personnes",
      table: "Table",
      reference: "Référence",
      thanks: "Merci.",
      footer: "Ce message confirme une modification de réservation auprès du commerçant.",
      designMark: RESERVATION_RESCHEDULE_DESIGN_MARK,
    }),
  };
};

const getDefaultReservationOwnerReviewTemplate = () => {
  return {
    id: DEFAULT_RESERVATION_OWNER_REVIEW_TEMPLATE_ID,
    name: "reservation_owner_review",
    subject: "Reservation awaiting confirmation - {{storeName}} - {{reservationDate}} {{reservationTime}}",
    htmlContent: reservationEmailDocument({
      lang: "en",
      heading: "Reservation awaiting confirmation",
      hello: "Reservation for ",
      lead: "A reservation is awaiting confirmation.",
      date: "Date",
      time: "Time",
      guests: "Party size",
      table: "Table",
      phone: "Phone",
      emailAddress: "Email",
      notesLabel: "Notes",
      reference: "Reference",
      review: "Review reservation",
      thanks: "Thank you.",
      footer: "This message requests confirmation of a reservation.",
      designMark: RESERVATION_OWNER_REVIEW_DESIGN_MARK,
    }),
  };
};

const getDefaultReservationOwnerReviewTemplateFr = () => {
  return {
    id: DEFAULT_RESERVATION_OWNER_REVIEW_TEMPLATE_ID_FR,
    name: "reservation_owner_review_fr",
    subject: "Réservation en attente de confirmation - {{storeName}} - {{reservationDate}} {{reservationTime}}",
    htmlContent: reservationEmailDocument({
      lang: "fr",
      heading: "Réservation en attente de confirmation",
      hello: "Réservation pour ",
      lead: "Une réservation est en attente de confirmation.",
      date: "Date",
      time: "Heure",
      guests: "Nombre de personnes",
      table: "Table",
      phone: "Téléphone",
      emailAddress: "Courriel",
      notesLabel: "Notes",
      reference: "Référence",
      review: "Examiner la réservation",
      thanks: "Merci.",
      footer: "Ce message demande la confirmation d'une réservation.",
      designMark: RESERVATION_OWNER_REVIEW_DESIGN_MARK,
    }),
  };
};

const getDefaultReservationProposalTemplate = () => {
  return {
    id: DEFAULT_RESERVATION_PROPOSAL_TEMPLATE_ID,
    name: "reservation_time_proposed_customer",
    subject: "Alternative time offered - {{storeName}} - {{proposedSchedule}}",
    htmlContent: reservationEmailDocument({
      lang: "en",
      heading: "Alternative time offered",
      hello: "Hello ",
      lead: "An alternative time is offered: {{proposedSchedule}}. The requested time remains {{reservationDate}} at {{reservationTime}}.",
      date: "Requested date",
      time: "Requested time",
      guests: "Party size",
      table: "Table",
      offered: "Offered time",
      reference: "Reference",
      review: "Review this offer",
      thanks: "Thank you.",
      footer: "This message offers a different time for a reservation.",
      designMark: RESERVATION_PROPOSAL_DESIGN_MARK,
    }),
  };
};

const getDefaultReservationProposalTemplateFr = () => {
  return {
    id: DEFAULT_RESERVATION_PROPOSAL_TEMPLATE_ID_FR,
    name: "reservation_time_proposed_customer_fr",
    subject: "Autre heure proposée - {{storeName}} - {{proposedSchedule}}",
    htmlContent: reservationEmailDocument({
      lang: "fr",
      heading: "Autre heure proposée",
      hello: "Bonjour ",
      lead: "Une autre heure est proposée : {{proposedSchedule}}. L'heure demandée demeure le {{reservationDate}} à {{reservationTime}}.",
      date: "Date demandée",
      time: "Heure demandée",
      guests: "Nombre de personnes",
      table: "Table",
      offered: "Heure proposée",
      reference: "Référence",
      review: "Examiner cette proposition",
      thanks: "Merci.",
      footer: "Ce message propose une autre heure pour une réservation.",
      designMark: RESERVATION_PROPOSAL_DESIGN_MARK,
    }),
  };
};

const getDefaultReservationCreatedTemplate = () => {
  const confirmed = getDefaultReservationConfirmedTemplate();
  return {
    id: DEFAULT_RESERVATION_CREATED_TEMPLATE_ID,
    name: "reservation_created_customer",
    subject: confirmed.subject,
    htmlContent: confirmed.htmlContent,
  };
};

const getDefaultReservationCreatedTemplateFr = () => {
  const confirmed = getDefaultReservationConfirmedTemplateFr();
  return {
    id: DEFAULT_RESERVATION_CREATED_TEMPLATE_ID_FR,
    name: "reservation_created_customer_fr",
    subject: confirmed.subject,
    htmlContent: confirmed.htmlContent,
  };
};

interface SmtpConfig {
  id: string;
  name: string;
  smtpServer: string;
  smtpPort: number;
  smtpUsername: string;
  smtpPassword: string;
  fromEmail: string;
  fromName?: string;
  isActive: boolean;
}

interface TestEmailLog {
  id: string;
  timestamp: Date | any; // Can be Date or Firestore Timestamp
  recipient: string;
  templateName: string;
  status: "success" | "error";
  message: string;
  errorDetails?: string;
  messageId?: string | null; // Message ID from Brevo for webhook matching
  createdAt?: any; // For Firestore
}

interface TemplateVersion {
  number: number;
  subject: string;
  htmlContent: string;
  changedAt?: any;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  htmlContent: string;
  group?: TemplateGroupId;
  defaultSubject: string;
  defaultHtmlContent: string;
  activeVersion: number;
  versions: TemplateVersion[];
  createdAt?: any;
  lastUpdated?: any;
  updatedBy?: string;
}

function readVersions(raw: unknown): TemplateVersion[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      const version = item as Record<string, any>;
      return {
        number: Number(version.number) || index + 1,
        subject: String(version.subject || ""),
        htmlContent: String(version.htmlContent || ""),
        changedAt: version.changedAt,
      };
    })
    .sort((a, b) => a.number - b.number);
}

function versionChangedLabel(value: any): string {
  if (!value) return "";
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

// Default POSClosingReceipt template HTML
const getDefaultPOSClosingReceiptTemplate = (): string => {
  return `<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Reçu de Transaction</title>
    <style>
        .email-receipt-container * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        .email-receipt-container {
            font-family: 'Courier New', monospace;
            background-color: #f5f5f5;
            padding: 20px;
        }
        .receipt-container {
            max-width: 400px;
            margin: 0 auto;
            background: white;
            border: 2px solid #000;
            padding: 20px;
        }
        .header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 1px solid #000;
            padding-bottom: 15px;
        }
        .store-name {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
        }
        .store-address {
            font-size: 12px;
            line-height: 1.4;
        }
        .transaction-info {
            margin-bottom: 15px;
            font-size: 12px;
        }
        .transaction-info div {
            margin-bottom: 3px;
        }
        .items-section {
            margin-bottom: 15px;
        }
        .item-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
            font-size: 12px;
            border-bottom: 1px dotted #ccc;
            padding-bottom: 5px;
        }
        .item-qty {
            width: 30px;
        }
        .item-desc {
            flex: 1;
            margin: 0 10px;
        }
        .item-price {
            text-align: right;
            width: 80px;
        }
        .subtotal {
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px solid #000;
            font-size: 12px;
        }
        .tax-info {
            margin: 10px 0;
            font-size: 11px;
        }
        .tax-info div {
            margin-bottom: 3px;
        }
        .totals {
            margin-top: 15px;
            padding-top: 15px;
            border-top: 2px solid #000;
        }
        .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
            font-size: 13px;
        }
        .total-final {
            font-weight: bold;
            font-size: 15px;
            margin-top: 5px;
        }
        .payment-method {
            margin: 15px 0;
            font-size: 12px;
            text-align: center;
        }
        .greeting {
            text-align: center;
            margin: 15px 0;
            font-size: 13px;
            font-weight: bold;
        }
        .separator {
            border-top: 1px solid #000;
            margin: 15px 0;
        }
        .qr-section {
            text-align: center;
            margin: 20px 0;
        }
        .qr-code {
            width: 150px;
            height: 150px;
            margin: 10px auto;
            background: #f0f0f0;
            border: 1px solid #000;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
        }
        .online-link {
            margin-top: 10px;
            font-size: 11px;
        }
        .online-link a {
            color: #0066cc;
            text-decoration: underline;
        }
        .transaction-ref {
            margin-top: 10px;
            font-size: 10px;
            text-align: center;
        }
    </style>
</head>
<body>
    <div class="email-receipt-container">
    <div class="receipt-container">
        <div class="header">
            <div class="store-name">{{storeName}}</div>
            <div class="store-address">
                {{storeAddress}}<br>
                {{storeCity}}, {{storeProvince}} {{storePostalCode}}
            </div>
        </div>

        <div class="transaction-info">
            <div><strong>Date:</strong> {{transactionDate}} {{transactionTime}}</div>
            <div><strong>Transaction #:</strong> {{transactionNumber}}</div>
            {{#if tableName}}<div><strong>Table:</strong> {{tableName}}</div>{{/if}}
        </div>

        <div class="items-section">
            {{#each items}}
            <div class="item-row">
                <span class="item-qty">{{amount}}</span>
                <span class="item-desc">{{name}}{{#if description}} - {{description}}{{/if}}</span>
                <span class="item-price">{{price}} $</span>
            </div>
            {{/each}}
        </div>

        <div class="subtotal">
            <div style="text-align: right;"><strong>SOUS-TOTAL:</strong> {{subtotal}} $</div>
        </div>

        {{#if feesAndDiscounts}}
        <div class="tax-info">
            {{#each feesAndDiscounts}}
            {{#if amount}}
            <div style="text-align: right;">{{@key}}: {{amount}} $</div>
            {{/if}}
            {{/each}}
        </div>
        {{/if}}

        <div class="tax-info">
            {{#if tpsNumber}}<div><strong>TPS:</strong> {{tpsNumber}} RT 0001</div>{{/if}}
            {{#if tvqNumber}}<div><strong>TVQ:</strong> {{tvqNumber}} TQ 0001</div>{{/if}}
        </div>

        <div class="payment-method">
            <strong>{{paymentMethod}}</strong>
        </div>

        <div class="greeting">
            Merci et bonne journée!
        </div>

        <div class="separator"></div>

        <div class="totals">
            {{#if tpsAmount}}<div class="total-row"><span>TPS:</span><span>{{tpsAmount}} $</span></div>{{/if}}
            {{#if tvqAmount}}<div class="total-row"><span>TVQ:</span><span>{{tvqAmount}} $</span></div>{{/if}}
            <div class="total-row total-final">
                <span>TOTAL:</span>
                <span>{{grandTotal}} $</span>
            </div>
        </div>

        <div class="separator"></div>

        <div class="qr-section">
            <div style="font-weight: bold; margin-bottom: 10px;">PAIEMENT REÇU</div>
            <div class="qr-code">
                <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://nuagepos.com/transaction/{{transactionReference}}" alt="QR Code" style="width: 100%; height: 100%; object-fit: contain;" />
            </div>
            <div class="online-link">
                <a href="https://nuagepos.com/">Consulter la transaction en ligne</a>
            </div>
            <div class="transaction-ref">
                <div>Date: {{transactionDate}} {{transactionTime}}</div>
                <div>Référence: {{transactionReference}}</div>
                <div>ID: {{transactionId}}</div>
            </div>
        </div>
    </div>
    </div>
</body>
</html>`;
};

// Dummy data for preview
const getDummyOrderData = () => {
  return {
    storeName: "Entreprise ABC",
    storeAddress: "3800, rue de Marly",
    storeCity: "Québec",
    storeProvince: "Québec",
    storePostalCode: "G1X 4A5",
    transactionDate: "2022-01-01",
    transactionTime: "08:30:35",
    transactionNumber: "TRANSACTION #100005",
    tableName: "23",
    items: [
      { amount: 1, name: "Item 1", description: "", price: "2,00" },
      { amount: 1, name: "Item 2", description: "", price: "3,00" },
    ],
    subtotal: "5,00",
    feesAndDiscounts: {},
    tpsNumber: "123456789",
    tvqNumber: "1234567890",
    paymentMethod: "CARTE DE CRÉDIT",
    tpsAmount: "0,25",
    tvqAmount: "0,50",
    grandTotal: "5,75",
    transactionReference: "HGSH-7653-EWEE-0865",
    transactionId: "0001-2345-6789",
    customerName: "Alex Martin",
    reservationDate: "2026-09-30",
    reservationTime: "19:00",
    numberOfGuests: "4",
    reservationId: "RES26093001",
    rescheduleUrl: "https://nuagepos.com",
    cancelUrl: "https://nuagepos.com",
  };
};

// Simple template variable replacement (basic version, can be enhanced with Handlebars later)
const replaceTemplateVariables = (html: string, data: any): string => {
  let result = html;
  
  // Handle items loop first (before other replacements)
  if (data.items && Array.isArray(data.items)) {
    const itemsRegex = /\{\{#each items\}\}([\s\S]*?)\{\{\/each\}\}/g;
    let match;
    while ((match = itemsRegex.exec(result)) !== null) {
      const itemTemplate = match[1];
      const itemsHtml = data.items.map((item: any) => {
        let itemHtml = itemTemplate;
        Object.keys(item).forEach(key => {
          const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
          itemHtml = itemHtml.replace(regex, String(item[key] || ''));
        });
        // Remove conditional helpers within items
        itemHtml = itemHtml.replace(/\{\{#if[^}]*\}\}/g, '');
        itemHtml = itemHtml.replace(/\{\{\/if\}\}/g, '');
        return itemHtml;
      }).join('');
      result = result.replace(match[0], itemsHtml);
    }
  }

  // Handle feesAndDiscounts loop if exists
  if (data.feesAndDiscounts && typeof data.feesAndDiscounts === 'object') {
    const feesRegex = /\{\{#each feesAndDiscounts\}\}([\s\S]*?)\{\{\/each\}\}/g;
    let match;
    while ((match = feesRegex.exec(result)) !== null) {
      const feeTemplate = match[1];
      const feesHtml = Object.entries(data.feesAndDiscounts).map(([key, value]: [string, any]) => {
        let feeHtml = feeTemplate;
        feeHtml = feeHtml.replace(/\{\{@key\}\}/g, key);
        if (value && typeof value === 'object') {
          Object.keys(value).forEach(prop => {
            const regex = new RegExp(`\\{\\{${prop}\\}\\}`, 'g');
            feeHtml = feeHtml.replace(regex, String(value[prop] || ''));
          });
        }
        feeHtml = feeHtml.replace(/\{\{#if[^}]*\}\}/g, '');
        feeHtml = feeHtml.replace(/\{\{\/if\}\}/g, '');
        return feeHtml;
      }).join('');
      result = result.replace(match[0], feesHtml);
    }
  }

  // Replace simple variables
  Object.keys(data).forEach(key => {
    if (key !== 'items' && key !== 'feesAndDiscounts') {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      result = result.replace(regex, String(data[key] || ''));
    }
  });

  // Remove remaining template helpers
  result = result.replace(/\{\{#if[^}]*\}\}/g, '');
  result = result.replace(/\{\{\/if\}\}/g, '');
  result = result.replace(/\{\{#each[^}]*\}\}/g, '');
  result = result.replace(/\{\{\/each\}\}/g, '');

  return result;
};

const EmailTemplateManagement: React.FC = () => {
  const toast = useToast();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);

  // Initialize with default template so it shows immediately
  const [templateName, setTemplateName] = useState("");
  const [templateGroup, setTemplateGroup] = useState<TemplateGroupId>("user");
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateHtml, setTemplateHtml] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");

  // Inner tabs for editor/preview/test
  const [expandedGroups, setExpandedGroups] = useState<number[]>([2]);
  const [activeTab, setActiveTab] = useState(0);
  const [adding, setAdding] = useState(false);

  // Test email states
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [activeSmtpConfig, setActiveSmtpConfig] = useState<SmtpConfig | null>(null);
  const [emailProvider, setEmailProvider] = useState<"ses" | "brevo">("ses");
  const [testEmailLogs, setTestEmailLogs] = useState<TestEmailLog[]>([]);
  const [emailLogoUrl, setEmailLogoUrl] = useState("");
  const [logoOpen, setLogoOpen] = useState(false);
  const [logoSaving, setLogoSaving] = useState(false);

  const bgPage = useColorModeValue("gray.50", "gray.900");
  const bgCard = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const textSecondary = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const activeBg = useColorModeValue("blue.50", "blue.900");
  const errorBg = useColorModeValue("red.50", "red.900");

  const loadTemplates = async () => {
    try {
      const templatesRef = collection(db, EMAIL_TEMPLATES_COLLECTION);
      const snapshot = await getDocs(templatesRef);
      const loaded: EmailTemplate[] = [];

      snapshot.forEach((d) => {
        const data = d.data() as any;
        const subject = data.subject || "";
        const htmlContent = data.htmlContent || "";
        const defaultLocked = data.defaultLocked === true;
        loaded.push({
          id: d.id,
          name: data.name || d.id,
          subject,
          htmlContent,
          group: templateGroupId({ id: d.id, name: data.name || d.id, group: data.group }),
          defaultSubject: defaultLocked ? String(data.defaultSubject || "") : subject,
          defaultHtmlContent: defaultLocked ? String(data.defaultHtmlContent || "") : htmlContent,
          activeVersion: Number(data.activeVersion || 0),
          versions: readVersions(data.versions),
          createdAt: data.createdAt,
          lastUpdated: data.lastUpdated,
          updatedBy: data.updatedBy,
        });
        if (!defaultLocked && !isPosClosingTemplate({ id: d.id, name: data.name || d.id })) {
          updateDoc(doc(db, EMAIL_TEMPLATES_COLLECTION, d.id), {
            defaultSubject: subject,
            defaultHtmlContent: htmlContent,
            defaultLocked: true,
            activeVersion: Number(data.activeVersion || 0),
            versions: Array.isArray(data.versions) ? data.versions : [],
            group: templateGroupId({ id: d.id, name: data.name || d.id, group: data.group }),
          }).catch((error) => console.error("Error saving template default:", error));
        }
      });

      const visible = loaded.filter((item) => !isPosClosingTemplate(item));
      visible.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTemplates(visible);

      if ((!selectedTemplate || isPosClosingTemplate(selectedTemplate)) && visible[0]) {
        handleTemplateSelect(visible[0]);
      }
      return visible;
    } catch (error) {
      console.error("Error loading templates:", error);
      return [];
    }
  };

  const ensureDefaultTransactionalTemplates = async () => {
    try {
      const brandSnap = await getDoc(doc(db, BRAND_LOGO_COLLECTION, BRAND_LOGO_DOC_ID));
      const savedLogo = brandSnap.exists() ? String(brandSnap.data()?.emailLogoUrl || "").trim() : "";
      const withLogo = (html: string) => (savedLogo.startsWith("https://") ? applyEmailLogo(html, savedLogo) : html);
      const defaults = [
        getDefaultOrderCreatedTemplate(),
        getDefaultOrderCreatedTemplateFr(),
        getDefaultOrderCancelledTemplate(),
        getDefaultOrderCancelledTemplateFr(),
        getDefaultReservationCreatedTemplate(),
        getDefaultReservationCreatedTemplateFr(),
        getDefaultReservationConfirmedTemplate(),
        getDefaultReservationConfirmedTemplateFr(),
        getDefaultReservationCancelledTemplate(),
        getDefaultReservationCancelledTemplateFr(),
        getDefaultReservationRescheduledTemplate(),
        getDefaultReservationRescheduledTemplateFr(),
        getDefaultReservationOwnerReviewTemplate(),
        getDefaultReservationOwnerReviewTemplateFr(),
        getDefaultReservationProposalTemplate(),
        getDefaultReservationProposalTemplateFr(),
        ...STATION_REGISTRATION_TEMPLATES,
        ...DOCUMENT_EMAIL_TEMPLATES,
        ...MEMBER_VERIFICATION_TEMPLATES,
        ...PASSWORD_RESET_TEMPLATES,
      ];
      for (const tpl of defaults) {
        const ref = doc(db, EMAIL_TEMPLATES_COLLECTION, tpl.id);
        const existing = await getDoc(ref);
        const stationDesign = tpl.id.startsWith("station_registration");
        const documentDesign = tpl.id.startsWith("document_");
        const memberDesign = tpl.id.startsWith("member_email_verification");
        const passwordResetDesign = tpl.id.startsWith("password_reset");
        const orderDesign = tpl.id === DEFAULT_ORDER_CREATED_TEMPLATE_ID
          || tpl.id === DEFAULT_ORDER_CREATED_TEMPLATE_ID_FR
          || tpl.id === DEFAULT_ORDER_CANCELLED_TEMPLATE_ID
          || tpl.id === DEFAULT_ORDER_CANCELLED_TEMPLATE_ID_FR;
        if (existing.exists()) {
          const html = String(existing.data()?.htmlContent || "");
          const lockedHtml = String(existing.data()?.defaultHtmlContent || "");
          const activeVersion = Number(existing.data()?.activeVersion || 0);
          const needsStationDesign = stationDesign && activeVersion === 0 && !html.includes("nuage-station-registration-design:3");
          const needsOrderDesign = orderDesign && activeVersion === 0
            && !html.includes(ORDER_DESIGN_MARK)
            && !lockedHtml.includes(ORDER_DESIGN_MARK);
          const needsDocumentDesign = documentDesign && activeVersion === 0
            && !html.includes(DOCUMENT_EMAIL_MARK)
            && !lockedHtml.includes(DOCUMENT_EMAIL_MARK);
          const needsMemberDesign = memberDesign && activeVersion === 0
            && !html.includes("nuage-member-verification-design:1")
            && !lockedHtml.includes("nuage-member-verification-design:1");
          const needsPasswordResetDesign = passwordResetDesign && activeVersion === 0
            && !html.includes(PASSWORD_RESET_DESIGN_MARK)
            && !lockedHtml.includes(PASSWORD_RESET_DESIGN_MARK);
          if (needsStationDesign || needsOrderDesign || needsDocumentDesign || needsMemberDesign || needsPasswordResetDesign) {
            const htmlContent = withLogo(tpl.htmlContent);
            await updateDoc(ref, {
              subject: tpl.subject,
              htmlContent,
              defaultSubject: tpl.subject,
              defaultHtmlContent: htmlContent,
              defaultLocked: true,
              group: documentDesign ? "document" : stationDesign ? "station" : (memberDesign || passwordResetDesign) ? "user" : "order",
              lastUpdated: serverTimestamp(),
              updatedBy: currentUser?.email || "unknown",
            });
          }
          continue;
        }

        const htmlContent = withLogo(tpl.htmlContent);
        await setDoc(ref, {
          id: tpl.id,
          name: tpl.name,
          subject: tpl.subject,
          htmlContent,
          defaultSubject: tpl.subject,
          defaultHtmlContent: htmlContent,
          defaultLocked: true,
          activeVersion: 0,
          versions: [],
          group: tpl.id.startsWith("document_")
            ? "document"
            : tpl.id.startsWith("station_registration")
              ? "station"
              : tpl.id.startsWith("member_email_verification") || tpl.id.startsWith("password_reset")
                ? "user"
                : templateGroupId({ id: tpl.id, name: tpl.name }),
          createdAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          updatedBy: currentUser?.email || "unknown",
        });
      }
    } catch (error) {
      console.error("Error ensuring default transactional templates:", error);
    }
  };

  const loadActiveSmtpConfig = async () => {
    try {
      const configDocRef = doc(db, EMAIL_CONFIG_COLLECTION, "global");
      const configDoc = await getDoc(configDocRef);

      if (configDoc.exists()) {
        const data: any = configDoc.data();
        setEmailProvider(data.emailProvider === "brevo" ? "brevo" : "ses");
        const activeSmtpId = data.activeSmtpId;
        const smtpConfigs = data.smtpConfigs || [];

        if (activeSmtpId && smtpConfigs.length > 0) {
          const activeConfig = smtpConfigs.find((c: SmtpConfig) => c.id === activeSmtpId);
          if (activeConfig) {
            setActiveSmtpConfig(activeConfig);
          }
        }
      }
    } catch (error) {
      console.error("Error loading active SMTP config:", error);
    }
  };

  const handleTemplateSelect = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setTemplateName(template.name);
    setTemplateGroup(templateGroupId(template));
    setTemplateSubject(template.subject);
    setTemplateHtml(template.htmlContent);
    setAdding(false);
    setActiveTab(0);
  };

  const startAdding = () => {
    if (!selectedTemplate) return;
    setTemplateName(selectedTemplate.name);
    setTemplateGroup(templateGroupId(selectedTemplate));
    setTemplateSubject(selectedTemplate.subject);
    setTemplateHtml(selectedTemplate.htmlContent);
    setAdding(true);
    setActiveTab(0);
  };

  const cancelAdding = () => {
    if (selectedTemplate) {
      setTemplateName(selectedTemplate.name);
      setTemplateGroup(templateGroupId(selectedTemplate));
      setTemplateSubject(selectedTemplate.subject);
      setTemplateHtml(selectedTemplate.htmlContent);
      setAdding(false);
      return;
    }
    setAdding(false);
    if (templates[0]) handleTemplateSelect(templates[0]);
  };

  const handleCreate = async () => {
    if (!templateName.trim()) {
      toast({ title: "Template name is required", status: "error", duration: 3000, isClosable: true });
      return;
    }
    setLoading(true);
    try {
      const templateId = templateName.trim();
      const subject = templateSubject.trim() || "Email from {{storeName}}";
      const htmlContent = templateHtml.trim();
      const templateData = {
        id: templateId,
        name: templateId,
        group: templateGroup,
        subject,
        htmlContent,
        defaultSubject: subject,
        defaultHtmlContent: htmlContent,
        defaultLocked: true,
        activeVersion: 0,
        versions: [],
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      };
      await setDoc(doc(db, EMAIL_TEMPLATES_COLLECTION, templateId), templateData);
      const visible = await loadTemplates();
      const saved = visible?.find((item) => item.id === templateId);
      if (saved) handleTemplateSelect(saved);
      setAdding(false);
      toast({ title: "Template saved", description: "This default is the message that will be sent.", status: "success", duration: 3000, isClosable: true });
    } catch (error: any) {
      toast({ title: "Could not save", description: error.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleLoadDefault = () => {
    if (!selectedTemplate) return;
    setTemplateSubject(selectedTemplate.defaultSubject);
    setTemplateHtml(selectedTemplate.defaultHtmlContent);
    toast({ title: "Default loaded", description: "The saved default was not changed.", status: "info", duration: 2500, isClosable: true });
  };

  const handleAddVersion = async () => {
    if (!selectedTemplate) return;
    if (!templateSubject.trim() || !templateHtml.trim()) {
      toast({ title: "Subject and HTML are required", status: "error", duration: 3000, isClosable: true });
      return;
    }
    setLoading(true);
    try {
      const nextNumber = selectedTemplate.versions.reduce((max, version) => Math.max(max, version.number), 0) + 1;
      const subject = templateSubject.trim();
      const htmlContent = templateHtml.trim();
      const versions = [
        ...selectedTemplate.versions.map((version) => ({
          number: version.number,
          subject: version.subject,
          htmlContent: version.htmlContent,
          changedAt: version.changedAt || null,
        })),
        { number: nextNumber, subject, htmlContent, changedAt: Timestamp.now() },
      ];
      await updateDoc(doc(db, EMAIL_TEMPLATES_COLLECTION, selectedTemplate.id), {
        name: templateName.trim() || selectedTemplate.name,
        group: templateGroup,
        subject,
        htmlContent,
        activeVersion: nextNumber,
        versions,
        lastUpdated: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      });
      const visible = await loadTemplates();
      const saved = visible?.find((item) => item.id === selectedTemplate.id);
      if (saved) handleTemplateSelect(saved);
      setAdding(false);
      toast({
        title: `Added version ${nextNumber} is active`,
        description: "This is the email that will be sent.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({ title: "Could not add version", description: error.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleUseVersion = async (versionNumber: number) => {
    if (!selectedTemplate) return;
    const chosen = versionNumber === 0
      ? { subject: selectedTemplate.defaultSubject, htmlContent: selectedTemplate.defaultHtmlContent }
      : selectedTemplate.versions.find((version) => version.number === versionNumber);
    if (!chosen) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, EMAIL_TEMPLATES_COLLECTION, selectedTemplate.id), {
        subject: chosen.subject,
        htmlContent: chosen.htmlContent,
        activeVersion: versionNumber,
        lastUpdated: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      });
      const visible = await loadTemplates();
      const saved = visible?.find((item) => item.id === selectedTemplate.id);
      if (saved) handleTemplateSelect(saved);
      setAdding(false);
      toast({
        title: versionNumber === 0 ? "Default version is active" : `Added version ${versionNumber} is active`,
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error: any) {
      toast({ title: "Could not use this version", description: error.message, status: "error", duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId: string) => {
    if (BUILTIN_TEMPLATE_IDS.has(templateId)) return;
    if (!window.confirm("Delete this template?")) return;
    setLoading(true);
    try {
      await deleteDoc(doc(db, EMAIL_TEMPLATES_COLLECTION, templateId));
      await loadTemplates();
      toast({
        title: "Success",
        description: "Template deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error deleting template:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete template",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const saveTestEmailLog = async (log: TestEmailLog) => {
    try {
      const logData: any = {
        timestamp: serverTimestamp(),
        recipient: log.recipient ? log.recipient.trim().toLowerCase() : log.recipient,
        templateName: log.templateName,
        status: log.status,
        message: log.message,
        errorDetails: log.errorDetails || null,
        createdAt: serverTimestamp(),
      };

      if (log.messageId) {
        logData.messageId = log.messageId;
      }

      await setDoc(doc(db, EMAIL_TEST_LOGS_COLLECTION, log.id), logData);
    } catch (error) {
      console.error("Error saving test email log:", error);
    }
  };

  const deleteTestEmailLog = async (logId: string) => {
    try {
      await deleteDoc(doc(db, EMAIL_TEST_LOGS_COLLECTION, logId));
      setTestEmailLogs((prev) => prev.filter((log) => log.id !== logId));
      toast({
        title: "Success",
        description: "Log entry deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting test email log:", error);
    }
  };

  const handleSendTestEmail = async () => {
    if (emailProvider === "brevo" && !activeSmtpConfig) {
      toast({
        title: "Error",
        description: "No active SMTP configuration found",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!testEmailAddress.trim()) {
      toast({
        title: "Validation Error",
        description: "Recipient email is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmailAddress.trim())) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid email address",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (emailProvider === "brevo" && (!activeSmtpConfig?.smtpPassword || !activeSmtpConfig?.fromEmail)) {
      toast({
        title: "Error",
        description: "SMTP configuration is incomplete. Please ensure API key and From Email are set.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const dummyData = getDummyOrderData();
    const htmlContent = replaceTemplateVariables(templateHtml, dummyData);
    const subject = replaceTemplateVariables(templateSubject, dummyData);

    setSendingTestEmail(true);
    const logId = `${Date.now()}`;
    try {
      const sendConfiguredEmail = httpsCallable<any, { ok: boolean; message?: string; messageId?: string }>(functions, "sendConfiguredEmail");
      const { data } = await sendConfiguredEmail({
        to: testEmailAddress.trim(),
        toName: String(dummyData.customerName || "Test Recipient"),
        fromName: `${String(dummyData.storeName || "").trim()} Booking`.trim(),
        subject: subject || "Test Email",
        html: htmlContent,
        text: `Test email for template: ${templateName}`,
      });
      if (!data.ok) {
        throw new Error(data.message || "Email was not sent");
      }
      const messageId = data.messageId ? String(data.messageId) : null;

      await saveTestEmailLog({
        id: logId,
        timestamp: new Date(),
        recipient: testEmailAddress.trim(),
        templateName: templateName,
        status: "success",
        message: `Test email sent successfully to ${testEmailAddress.trim()}`,
        messageId,
      });

      toast({
        title: "Success",
        description: `Test email sent successfully to ${testEmailAddress.trim()}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setTestEmailAddress("");
    } catch (error: any) {
      console.error("Error sending test email:", error);
      await saveTestEmailLog({
        id: logId,
        timestamp: new Date(),
        recipient: testEmailAddress.trim(),
        templateName: templateName,
        status: "error",
        message: "Error sending test email",
        errorDetails: error?.message ? String(error.message) : "Unknown error",
      });
      toast({
        title: "Error Sending Email",
        description: error?.message || "Failed to send test email",
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleChooseEmailLogo = async (file: LogoFile) => {
    setLogoSaving(true);
    try {
      await setDoc(
        doc(db, BRAND_LOGO_COLLECTION, BRAND_LOGO_DOC_ID),
        { emailLogoUrl: file.publicUrl, updatedAt: serverTimestamp() },
        { merge: true }
      );
      const snapshot = await getDocs(collection(db, EMAIL_TEMPLATES_COLLECTION));
      const writes: Promise<unknown>[] = [];
      snapshot.forEach((entry) => {
        const data = entry.data() as Record<string, any>;
        const htmlContent = applyEmailLogo(String(data.htmlContent || ""), file.publicUrl);
        const defaultHtmlContent = applyEmailLogo(String(data.defaultHtmlContent || ""), file.publicUrl);
        let versionsChanged = false;
        const versions = Array.isArray(data.versions)
          ? data.versions.map((version: any) => {
              if (!version || typeof version !== "object") return version;
              const nextHtml = applyEmailLogo(String(version.htmlContent || ""), file.publicUrl);
              if (nextHtml === String(version.htmlContent || "")) return version;
              versionsChanged = true;
              return { ...version, htmlContent: nextHtml };
            })
          : data.versions;
        if (
          htmlContent === String(data.htmlContent || "")
          && defaultHtmlContent === String(data.defaultHtmlContent || "")
          && !versionsChanged
        ) {
          return;
        }
        writes.push(updateDoc(entry.ref, {
          htmlContent,
          defaultHtmlContent,
          ...(versionsChanged ? { versions } : {}),
          lastUpdated: serverTimestamp(),
          updatedBy: currentUser?.email || "unknown",
        }));
      });
      await Promise.all(writes);
      setEmailLogoUrl(file.publicUrl);
      const currentId = selectedTemplate?.id;
      const visible = await loadTemplates();
      const saved = visible?.find((item) => item.id === currentId);
      if (saved) handleTemplateSelect(saved);
      toast({
        title: "Email logo saved",
        description: "Every email template uses this logo.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Email logo was not saved",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLogoSaving(false);
    }
  };

  useEffect(() => {
    (async () => {
      await ensureDefaultTransactionalTemplates();
      await loadTemplates();
      const brandSnap = await getDoc(doc(db, BRAND_LOGO_COLLECTION, BRAND_LOGO_DOC_ID));
      if (brandSnap.exists()) setEmailLogoUrl(String(brandSnap.data()?.emailLogoUrl || ""));
    })();
    loadActiveSmtpConfig();

    const logsRef = collection(db, EMAIL_TEST_LOGS_COLLECTION);
    const logsQuery = query(logsRef, orderBy("timestamp", "desc"));

    const unsubscribe = onSnapshot(logsQuery, (querySnapshot) => {
      const loadedLogs: TestEmailLog[] = [];
      querySnapshot.forEach((d) => {
        const data: any = d.data();
        loadedLogs.push({
          id: d.id,
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp),
          recipient: data.recipient,
          templateName: data.templateName,
          status: data.status,
          message: data.message,
          errorDetails: data.errorDetails,
          messageId: data.messageId || null,
        });
      });

      setTestEmailLogs(loadedLogs);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (templateHtml) {
      const dummyData = getDummyOrderData();
      const rendered = replaceTemplateVariables(templateHtml, dummyData);
      setPreviewHtml(rendered);
    }
  }, [templateHtml]);

  useEffect(() => {
    if (!selectedTemplate) return;
    const visibleGroups = TEMPLATE_GROUPS.filter((group) =>
      templates.some((template) => templateGroupId(template) === group.id)
    );
    const index = visibleGroups.findIndex((group) => group.id === templateGroupId(selectedTemplate));
    if (index < 0) return;
    setExpandedGroups((current) => (current.includes(index) ? current : [...current, index]));
  }, [selectedTemplate, templates]);

  return (
    <PageShell
      title="Email Templates"
      description="Messages sent when an order or reservation is created or confirmed."
      wide
    >
      <VStack spacing={6} align="stretch">
        <HStack
          bg={bgCard}
          border="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
          justify="space-between"
          align="center"
          spacing={4}
        >
          <HStack spacing={4} minW={0}>
            {emailLogoUrl ? (
              <img
                src={previewSrcForUrl(emailLogoUrl)}
                alt="Email logo"
                style={{ height: 32, width: "auto", display: "block" }}
              />
            ) : null}
            <Box minW={0}>
              <Text fontWeight="600" color={textColor}>Email logo</Text>
              <Text fontSize="sm" color={textSecondary}>
                This logo is used on every email template.
              </Text>
            </Box>
          </HStack>
          <Button onClick={() => setLogoOpen(true)} isLoading={logoSaving} flexShrink={0}>
            Choose logo
          </Button>
        </HStack>
        <LogoCatalogModal
          isOpen={logoOpen}
          onClose={() => setLogoOpen(false)}
          selectedUrl={emailLogoUrl}
          hint="This logo is used on every email template."
          onSelect={handleChooseEmailLogo}
        />

        <HStack spacing={4} align="stretch">
          {/* Template List */}
          <Box w="320px" flexShrink={0} bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor} p={4}>
            <HStack justify="space-between" mb={4}>
              <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                Templates
              </Text>
              <Button
                size="sm"
                leftIcon={<AddIcon />}
                onClick={() => {
                  setSelectedTemplate(null);
                  setTemplateName("");
                  setTemplateGroup("user");
                  setTemplateSubject("");
                  setTemplateHtml("");
                  setAdding(true);
                  setActiveTab(0);
                }}
              >
                New
              </Button>
            </HStack>
            <Box maxH="640px" overflowY="auto">
            <Accordion
              allowMultiple
              index={expandedGroups}
              onChange={(next) => setExpandedGroups(Array.isArray(next) ? next : [next])}
              reduceMotion
            >
              {TEMPLATE_GROUPS.map((group) => ({
                ...group,
                items: templates.filter((template) => templateGroupId(template) === group.id),
              }))
                .filter((group) => group.items.length > 0)
                .map((group, index) => (
                  <AccordionItem key={group.id} border="none">
                    <AccordionButton px={1} py={2} borderRadius="md">
                      <Text flex="1" textAlign="left" fontSize="sm" fontWeight="600">
                        {group.label}
                      </Text>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel px={0} py={1}>
                      <VStack spacing={2} align="stretch">
                        {group.items.map((template) => (
                          <HStack
                            key={template.id}
                            spacing={2}
                            px={3}
                            py={2}
                            borderRadius="md"
                            bg={selectedTemplate?.id === template.id ? activeBg : "transparent"}
                            border="1px"
                            borderColor={selectedTemplate?.id === template.id ? "blue.300" : borderColor}
                            cursor="pointer"
                            onClick={() => handleTemplateSelect(template)}
                            _hover={{ bg: activeBg }}
                          >
                            <Text
                              flex="1"
                              minW={0}
                              noOfLines={1}
                              fontSize="sm"
                              fontWeight={selectedTemplate?.id === template.id ? "semibold" : "normal"}
                              title={template.name}
                            >
                              {compactTemplateLabel(template.name)}
                            </Text>
                            {!BUILTIN_TEMPLATE_IDS.has(template.id) ? (
                              <IconButton
                                aria-label="Delete"
                                icon={<DeleteIcon />}
                                size="xs"
                                variant="ghost"
                                flexShrink={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(template.id);
                                }}
                              />
                            ) : null}
                          </HStack>
                        ))}
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
            </Accordion>
            </Box>
          </Box>

          {/* Template Editor */}
          <Box flex={1} minW={0} bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor} p={6}>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm" color={textSecondary}>
                  {adding
                    ? "Edit mode. Save stores an added version and makes it the email that is sent."
                    : "The active version is the email that is sent."}
                </Text>
                <HStack>
                  {selectedTemplate && !adding ? (
                    <Button size="sm" onClick={startAdding}>Edit</Button>
                  ) : null}
                  {adding ? (
                    <Button size="sm" variant="outline" onClick={cancelAdding}>Cancel</Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<EmailIcon />}
                    onClick={() => setActiveTab(2)}
                    isDisabled={!templateName.trim() && !templateHtml.trim()}
                  >
                    Send test email
                  </Button>
                </HStack>
              </HStack>
              <FormControl>
                <FormLabel>Template Name</FormLabel>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., order_created_customer"
                  bg={adding ? "white" : "gray.50"}
                  isReadOnly={!adding}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Group</FormLabel>
                <Select
                  value={templateGroup}
                  bg={adding ? "white" : "gray.50"}
                  isDisabled={!adding}
                  onChange={(event) => setTemplateGroup(event.target.value as TemplateGroupId)}
                >
                  {TEMPLATE_GROUPS.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {selectedTemplate ? (
                <Box border="1px" borderColor={borderColor} borderRadius="md" p={3}>
                  <HStack justify="space-between" mb={3}>
                    <Text fontSize="sm" fontWeight="600">Versions</Text>
                    <Badge colorScheme="green">
                      {selectedTemplate.activeVersion === 0
                        ? "Active: Default version"
                        : `Active: Added version ${selectedTemplate.activeVersion}`}
                    </Badge>
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    <HStack>
                      <Text flex="1" fontSize="sm" fontWeight={selectedTemplate.activeVersion === 0 ? "600" : "normal"}>
                        Default version
                      </Text>
                      <Button size="xs" variant="outline" onClick={handleLoadDefault} isDisabled={!adding}>Load default</Button>
                      {selectedTemplate.activeVersion === 0 ? (
                        <Badge colorScheme="green">Active</Badge>
                      ) : (
                        <Button size="xs" onClick={() => handleUseVersion(0)}>Set active</Button>
                      )}
                    </HStack>
                    {[...selectedTemplate.versions].reverse().map((version) => (
                      <HStack key={version.number}>
                        <Text flex="1" fontSize="sm" noOfLines={1} fontWeight={selectedTemplate.activeVersion === version.number ? "600" : "normal"}>
                          Added version {version.number}
                          {versionChangedLabel(version.changedAt) ? ` · ${versionChangedLabel(version.changedAt)}` : ""}
                        </Text>
                        {selectedTemplate.activeVersion === version.number ? (
                          <Badge colorScheme="green">Active</Badge>
                        ) : (
                          <Button size="xs" onClick={() => handleUseVersion(version.number)}>Set active</Button>
                        )}
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              ) : null}

              <FormControl>
                <FormLabel>Email Subject</FormLabel>
                <Input
                  value={templateSubject}
                  onChange={(e) => setTemplateSubject(e.target.value)}
                  placeholder="e.g., Your order {{orderNumber}} has been received"
                  bg={adding ? "white" : "gray.50"}
                  isReadOnly={!adding}
                />
                <Text fontSize="xs" color={textSecondary} mt={1}>
                  Use {'{{'}variableName{'}}'} for dynamic content
                </Text>
              </FormControl>

              <Tabs index={activeTab} onChange={setActiveTab}>
                <TabList>
                  <Tab>HTML Editor</Tab>
                  <Tab>Preview</Tab>
                  <Tab>Test Email</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel px={0} pt={4}>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel>HTML Content</FormLabel>
                        <Textarea
                          value={templateHtml}
                          onChange={(e) => setTemplateHtml(e.target.value)}
                          placeholder="Paste your HTML template code here..."
                          bg={adding ? "white" : "gray.50"}
                          fontFamily="mono"
                          fontSize="sm"
                          minH="500px"
                          isReadOnly={!adding}
                        />
                        <Text fontSize="xs" color={textSecondary} mt={1}>
                          Use {'{{'}variableName{'}}'} for variables
                        </Text>
                      </FormControl>
                      {adding && selectedTemplate ? (
                        <Button onClick={handleAddVersion} isLoading={loading} loadingText="Saving...">
                          Save
                        </Button>
                      ) : null}
                      {adding && !selectedTemplate ? (
                        <Button onClick={handleCreate} isLoading={loading} loadingText="Saving...">
                          Save
                        </Button>
                      ) : null}
                    </VStack>
                  </TabPanel>

                  <TabPanel px={0} pt={4}>
                    <Box
                      border="1px"
                      borderColor={borderColor}
                      borderRadius="md"
                      p={4}
                      bg="white"
                      minH="500px"
                      overflow="auto"
                    >
                      {previewHtml ? (
                        <iframe
                          srcDoc={previewHtml}
                          style={{
                            width: "100%",
                            minHeight: "500px",
                            border: "none",
                            background: "white",
                          }}
                          title="Email Template Preview"
                        />
                      ) : (
                        <Text color={textSecondary}>Preview will appear here</Text>
                      )}
                    </Box>
                  </TabPanel>

                  <TabPanel px={0} pt={4}>
                    <VStack spacing={4} align="stretch">
                      {emailProvider === "brevo" && !activeSmtpConfig ? (
                        <Alert status="warning" borderRadius="md">
                          <AlertIcon />
                          <AlertDescription>
                            No active SMTP configuration found. Please configure SMTP settings first.
                          </AlertDescription>
                        </Alert>
                      ) : (
                        <>
                          <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            <AlertDescription>
                              Send a test email using the template "{templateName || "(unnamed)"}" with dummy data.
                              Provider: {emailProvider === "ses" ? "Amazon SES" : `Brevo (${activeSmtpConfig?.name || "SMTP"})`}
                            </AlertDescription>
                          </Alert>

                          <FormControl isRequired>
                            <FormLabel>Recipient Email</FormLabel>
                            <Input
                              type="email"
                              value={testEmailAddress}
                              onChange={(e) => setTestEmailAddress(e.target.value)}
                              placeholder="recipient@example.com"
                              bg="white"
                            />
                            <Text fontSize="xs" color={textSecondary} mt={1}>
                              Enter the email address where you want to receive the test email
                            </Text>
                          </FormControl>

                          <Button
                            colorScheme="green"
                            onClick={handleSendTestEmail}
                            isLoading={sendingTestEmail}
                            loadingText="Sending..."
                            isDisabled={!testEmailAddress.trim()}
                            size="lg"
                          >
                            Send Test Email
                          </Button>
                        </>
                      )}

                      <Box mt={4}>
                        <Text fontSize="md" fontWeight="semibold" mb={3} color={textColor}>
                          Test Email Log
                        </Text>
                        {testEmailLogs.length === 0 ? (
                          <Text color={textSecondary} fontSize="sm">
                            No test emails sent yet. Send a test email to see the log.
                          </Text>
                        ) : (
                          <VStack spacing={2} align="stretch" maxH="400px" overflowY="auto">
                            {testEmailLogs.map((log) => (
                              <Box
                                key={log.id}
                                p={3}
                                bg={bgCard}
                                border="1px"
                                borderColor={borderColor}
                                borderRadius="md"
                              >
                                <HStack justify="space-between" align="start" mb={2}>
                                  <HStack spacing={2} flex={1}>
                                    {log.status === "success" ? (
                                      <CheckIcon color="green.500" />
                                    ) : (
                                      <CloseIcon color="red.500" />
                                    )}
                                    <VStack align="start" spacing={0}>
                                      <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                                        {log.status === "success" ? "Success" : "Error"}
                                      </Text>
                                      <Text fontSize="xs" color={textSecondary}>
                                        {log.timestamp instanceof Date
                                          ? log.timestamp.toLocaleString()
                                          : new Date(log.timestamp).toLocaleString()}
                                      </Text>
                                    </VStack>
                                  </HStack>
                                  <HStack spacing={2}>
                                    <Badge
                                      colorScheme={log.status === "success" ? "green" : "red"}
                                      variant="subtle"
                                    >
                                      {log.status}
                                    </Badge>
                                    <IconButton
                                      aria-label="Delete log entry"
                                      icon={<DeleteIcon />}
                                      size="xs"
                                      variant="ghost"
                                      onClick={() => deleteTestEmailLog(log.id)}
                                    />
                                  </HStack>
                                </HStack>
                                <VStack align="stretch" spacing={1}>
                                  <Text fontSize="sm" color={textColor}>
                                    <strong>To:</strong> {log.recipient}
                                  </Text>
                                  <Text fontSize="sm" color={textColor}>
                                    <strong>Template:</strong> {log.templateName}
                                  </Text>
                                  <Text
                                    fontSize="sm"
                                    color={log.status === "error" ? "red.600" : textColor}
                                    fontWeight={log.status === "error" ? "semibold" : "normal"}
                                  >
                                    {log.message}
                                  </Text>
                                  {log.errorDetails && (
                                    <Box
                                      mt={2}
                                      p={3}
                                      bg={errorBg}
                                      borderRadius="md"
                                      border="1px"
                                      borderColor="red.200"
                                      maxH="200px"
                                      overflowY="auto"
                                    >
                                      <Text
                                        fontSize="xs"
                                        color="red.700"
                                        fontFamily="mono"
                                        whiteSpace="pre-wrap"
                                        wordBreak="break-word"
                                      >
                                        <strong>Error Details:</strong>
                                        {"\n"}
                                        {log.errorDetails}
                                      </Text>
                                    </Box>
                                  )}
                                </VStack>
                              </Box>
                            ))}
                          </VStack>
                        )}
                      </Box>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          </Box>
        </HStack>
      </VStack>
    </PageShell>
  );
};

export default EmailTemplateManagement;

